/**
 * Session last-thread for the agent panel.
 * Id alone is not enough after a full reload: workspace restore needs tags,
 * and those live in an in-memory map that starts empty.
 */

import { StorageKeys } from '@/lib/storage/keys'

export type AgentPanelLastThreadMeta = {
  threadId: string
  taggedOrganizationIds: string[]
  workspaceId: string | null
  organizationId?: string | null
}

function readJsonMeta(): AgentPanelLastThreadMeta | null {
  try {
    const raw = sessionStorage.getItem(StorageKeys.AGENT_PANEL_LAST_THREAD_META)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<AgentPanelLastThreadMeta>
    if (!parsed || typeof parsed.threadId !== 'string' || !parsed.threadId.trim()) return null
    const tags = Array.isArray(parsed.taggedOrganizationIds)
      ? parsed.taggedOrganizationIds.filter((id): id is string => typeof id === 'string' && Boolean(id.trim()))
      : []
    return {
      threadId: parsed.threadId,
      taggedOrganizationIds: tags,
      workspaceId: typeof parsed.workspaceId === 'string' ? parsed.workspaceId : null,
      organizationId: typeof parsed.organizationId === 'string' ? parsed.organizationId : null,
    }
  } catch {
    return null
  }
}

export function persistAgentPanelLastThreadId(threadId: string | null | undefined): void {
  if (!threadId || threadId.startsWith('new-')) return
  try {
    sessionStorage.setItem(StorageKeys.AGENT_PANEL_LAST_THREAD_ID, threadId)
  } catch {
    /* ignore */
  }
}

export function readAgentPanelLastThreadId(): string | null {
  try {
    const raw = sessionStorage.getItem(StorageKeys.AGENT_PANEL_LAST_THREAD_ID)
    if (!raw || raw.startsWith('new-')) return null
    return raw
  } catch {
    return null
  }
}

export function clearAgentPanelLastThreadId(threadId?: string): void {
  try {
    if (threadId) {
      const stored = sessionStorage.getItem(StorageKeys.AGENT_PANEL_LAST_THREAD_ID)
      if (stored !== threadId) return
    }
    sessionStorage.removeItem(StorageKeys.AGENT_PANEL_LAST_THREAD_ID)
    sessionStorage.removeItem(StorageKeys.AGENT_PANEL_LAST_THREAD_META)
  } catch {
    /* ignore */
  }
}

export function persistAgentPanelLastThreadMeta(meta: AgentPanelLastThreadMeta): void {
  if (!meta.threadId || meta.threadId.startsWith('new-')) return
  try {
    const storedId = sessionStorage.getItem(StorageKeys.AGENT_PANEL_LAST_THREAD_ID)
    if (storedId && storedId !== meta.threadId) return
    sessionStorage.setItem(StorageKeys.AGENT_PANEL_LAST_THREAD_META, JSON.stringify({
      threadId: meta.threadId,
      taggedOrganizationIds: meta.taggedOrganizationIds,
      workspaceId: meta.workspaceId,
      organizationId: meta.organizationId ?? null,
    }))
  } catch {
    /* ignore */
  }
}

/** Write tags for the current last-thread id (called from the in-memory cache). */
export function syncAgentPanelLastThreadMeta(thread: {
  id: string
  taggedOrganizationIds?: string[] | null
  workspaceId?: string | null
  organizationId?: string | null
}): void {
  try {
    const storedId = sessionStorage.getItem(StorageKeys.AGENT_PANEL_LAST_THREAD_ID)
    if (storedId !== thread.id) return
  } catch {
    return
  }
  persistAgentPanelLastThreadMeta({
    threadId: thread.id,
    taggedOrganizationIds: Array.isArray(thread.taggedOrganizationIds)
      ? thread.taggedOrganizationIds.filter(Boolean).map(String)
      : [],
    workspaceId: thread.workspaceId ?? null,
    organizationId: thread.organizationId ?? null,
  })
}

/** Session tags for the stored last-thread id, if they match. */
export function readAgentPanelLastThreadMeta(): AgentPanelLastThreadMeta | null {
  const storedId = readAgentPanelLastThreadId()
  if (!storedId) return null
  const meta = readJsonMeta()
  if (!meta || meta.threadId !== storedId) return null
  return meta
}
